<template>
    <div class="text-body leading-snug mb-32">
        <graph-title
            :show-tooltip="false"
            :title="$t('analytics.info.overview.title').toString()"
            class="mb-12"
            icon="share"
        ></graph-title>

        <div class="-mx-8 mt-24 flex justify-between gap-32">

            <div class="w-1/4 lowercase">
                <div :class="cardClasses">
                    <div class="relative w-full h-full">
                        <div v-if="fetching" class="absolute w-full h-full inset-0 flex items-center justify-center">
                            <loading color="blue" size="small"></loading>
                        </div>
                        <div :class="[ fetching ? 'opacity-0' : 'opacity-100' ]">
                            <span class="text-big mr-4 font-semibold">{{ (applications.total) }}</span>
                            <span class="text-heading-1">{{ $t('analytics.applications.title').toLowerCase() }}</span>

                            <div class="text-black text-body-sm pt-4">
                                <div class="flex items-center pt-4">
                                    <svg-icon
                                        class="opacity-75 text-secondary mr-8"
                                        size="16"
                                        type="check-circle"
                                    />
                                    <div>
                                        <span class="font-semibold">{{ (applications.hired) }}</span>
                                        <span>{{ $t('analytics.hired').toLowerCase() }}</span>
                                    </div>
                                </div>

                                <div class="flex items-center pt-4">
                                    <svg-icon
                                        class="opacity-75 text-semantic-error mr-8"
                                        size="16"
                                        type="do-not-disturb-on"
                                    />
                                    <div>
                                        <span class="font-semibold">{{ (applications.rejected) }}</span>
                                        <span>{{ $t('analytics.rejected').toLowerCase() }}</span>
                                    </div>
                                </div>

                                <div class="flex items-center pt-4">
                                    <progress-icon class="mr-8" size="16"></progress-icon>
                                    <div>
                                        <span class="font-semibold">{{ (applications.active) }}</span>
                                        <span>{{ $t('analytics.active').toLowerCase() }}</span>
                                    </div>
                                </div>

                                <div class="flex items-center pt-4">
                                    <svg-icon
                                        class="inline mr-8"
                                        size="16"
                                        type="schedule"
                                    />
                                    <div>
                                        <span class="font-semibold">{{ (applications.inactive) }}</span>
                                        <span>{{ $t('analytics.waiting_for_feedback').toLowerCase() }}</span>
                                    </div>
                                </div>

                                <div class="flex items-center pt-4">
                                    <svg-icon
                                        class="inline mr-8"
                                        size="16"
                                        type="anonymised"
                                    />
                                    <div>
                                        <span class="font-semibold">{{ (applications.anonymised) }}</span>
                                        <span>{{ $t('analytics.anonymised').toLowerCase() }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="w-1/4 lowercase">
                <div :class="cardClasses">
                    <div class="relative w-full h-full">
                        <div v-if="fetching" class="absolute w-full h-full inset-0 flex items-center justify-center">
                            <loading color="blue" size="small"></loading>
                        </div>
                        <div :class="[ fetching ? 'opacity-0' : 'opacity-100' ]">
                            <span class="text-big mr-4 font-semibold">{{ applications.leadTime }}</span>
                            <span class="text-heading-1">{{ $t('analytics.days') }}</span>

                            <div class="text-black text-body-sm pt-4">
                                {{ $t('analytics.average_lead_time').toLowerCase() }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="w-1/4 lowercase">
                <div :class="cardClasses">
                    <div class="relative w-full h-full">
                        <div v-if="fetching" class="absolute w-full h-full inset-0 flex items-center justify-center">
                            <loading color="blue" size="small"></loading>
                        </div>
                        <div :class="[ fetching ? 'opacity-0' : 'opacity-100' ]">
                            <span class="text-big mr-4 font-semibold">{{ jobs.total }}</span>
                            <span class="text-heading-1">{{ $t('analytics.jobs.title').toLowerCase() }}</span>

                            <div class="text-black text-body-sm pt-4">
                                <div class="flex items-center pt-4">
                                    <svg-icon
                                        class="opacity-75 text-secondary mr-8"
                                        size="16"
                                        type="check-circle"
                                    />
                                    <div>
                                        <span class="font-semibold">{{ jobs.published }}</span>
                                        <span>{{ $t('jobs.published') }}</span>
                                    </div>
                                </div>

                                <div class="flex items-center pt-4">
                                    <svg-icon
                                        class="opacity-75 text-semantic-error mr-8"
                                        size="16"
                                        type="do-not-disturb-on"
                                    />
                                    <div>
                                        <span class="font-semibold">{{ jobs.unpublished }}</span>
                                        <span>{{ $t('jobs.unpublished') }}</span>
                                    </div>
                                </div>

                                <div class="flex items-center pt-4">
                                    <svg-icon
                                        class="opacity-75 text-primary-80 mr-8"
                                        size="16"
                                        type="archive"
                                    />
                                    <div>
                                        <span class="font-semibold">{{ jobs.archived }}</span>
                                        <span>{{ $t('analytics.archived') }}</span>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <template>
                <div class="w-1/4 lowercase">
                    <div v-if="filterFields.jobIds.length === 1 && !!jobs.job"
                         :class="cardClasses">
                        <div class="relative w-full h-full">
                            <div v-if="fetching" class="absolute w-full h-full inset-0 flex items-center justify-center">
                                <loading color="blue" size="small"></loading>
                            </div>
                            <div :class="[ fetching ? 'opacity-0' : 'opacity-100' ]">
                                <p class="text-heading-2 text-primary mr-4 font-semibold">
                                    {{ moment(jobs.job.created_at).format('DD/MM/YYYY') }}
                                </p>
                                <span class="text-body-sm text-black">{{ $t('analytics.creation_date') }}</span>
                                <template v-if="jobs.job.published">
                                    <p class="text-heading-2 text-primary mr-4 font-semibold mt-16">
                                        {{ moment(jobs.job.last_published_at).format('DD/MM/YYYY') }}
                                    </p>
                                    <span class="text-body-sm text-black">{{ $t('analytics.publish_date') }}</span>
                                </template>
                            </div>
                        </div>
                    </div>
                    <div v-else :class="cardClasses">
                        <div class="relative w-full h-full">
                            <div v-if="fetching" class="absolute w-full h-full inset-0 flex items-center justify-center">
                                <loading color="blue" size="small"></loading>
                            </div>
                            <div :class="[ fetching ? 'opacity-0' : 'opacity-100' ]">
                                <span class="text-big mr-4 font-semibold">{{ jobs.fullFilled }}</span>
                                <span class="text-heading-1">{{ $t('analytics.filled_jobs').toLowerCase() }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import ProgressIcon from '../ui/ProgressIcon.vue';
import SvgIcon from '../ui/SvgIcon.vue';
import Loading from '../ui/Loading.vue';
import GraphTitle from "./GraphTitle";
import moment from 'moment';
import * as analytics from "../../services/AnalyticsService";

export default {
    components: {
        'svg-icon': SvgIcon,
        'loading': Loading,
        ProgressIcon,
        GraphTitle,
    },
    props: {
        filterFields: {type: Object},
        defaultCardClasses: {type: String},
    },
    data: function () {
        return {
            fetching: true,
            overviewData: null,
            applications: {},
            jobs: {},
        }
    },
    computed: {
        cardClasses() {
            // cut w-full and h-full from the defaultCardClasses, because we don't need this here
            return this.defaultCardClasses.replace('w-full', '');
        },
    },
    methods: {
        moment,
        fetchData() {
            this.fetching = true;
            analytics.overviewData(this.filterFields)
                .then(data => this.overviewData = data)
                .finally(() => this.handleData());
        },
        handleData() {
            this.applications = this.overviewData.applications;
            this.jobs = this.overviewData.jobs;
            this.fetching = false;
        }
    },
    watch: {
        'filterFields': {
            handler() {
                this.fetchData();
            },
            deep: true
        },
        fetching() {
            if (!this.fetching) {
                this.$emit('data-loaded');
            }
        }
    },
    mounted() {
        this.fetchData();
    }
}
</script>
